export interface VocabWord {
  id: string;
  word: string;
  pronunciation: string;
  partOfSpeech: string;
  definition: string;
  shortDef: string;
  mnemonic: string;
  examples: string[];
  youtubeLinks: { title: string; channel: string; duration: string }[];
  masteryLevel: number; // 0-100
  lastReviewed: string;
  status: "new" | "learning" | "almost" | "mastered";
}

export const SAMPLE_WORDS: VocabWord[] = [
  {
    id: "1",
    word: "ephemeral",
    pronunciation: "/ɪˈfem.ər.əl/",
    partOfSpeech: "adjective",
    definition: "Lasting for a very short time; transitory.",
    shortDef: "Lasting a very short time",
    mnemonic: "Think of an 'ephemeral eel' that slips through your fingers the moment you catch it — it's there for a second, then gone forever.",
    examples: [
      "The beauty of cherry blossoms is ephemeral, lasting only a week each spring.",
      "Social media trends are increasingly ephemeral, fading within days of going viral.",
    ],
    youtubeLinks: [
      { title: "Ephemeral Art Installations That Last Only Hours", channel: "Art Insider", duration: "8:42" },
      { title: "The Ephemeral Nature of Digital Culture", channel: "TED-Ed", duration: "5:17" },
    ],
    masteryLevel: 35,
    lastReviewed: "2 days ago",
    status: "learning",
  },
  {
    id: "2",
    word: "pernicious",
    pronunciation: "/pəˈnɪʃ.əs/",
    partOfSpeech: "adjective",
    definition: "Having a harmful effect, especially in a gradual or subtle way.",
    shortDef: "Having a subtle but harmful effect",
    mnemonic: "PERNICIOUS sounds like 'per-NASTY-ous' — something nasty that sneaks up on you slowly, like a slow poison.",
    examples: [
      "The pernicious effects of misinformation can undermine public trust in institutions.",
      "Scholars warned of the pernicious influence of propaganda on young minds.",
    ],
    youtubeLinks: [
      { title: "The Pernicious Rise of Junk Food Marketing to Kids", channel: "Vox", duration: "12:04" },
      { title: "Pernicious Anemia: Causes and Symptoms Explained", channel: "Medical Centric", duration: "6:33" },
    ],
    masteryLevel: 60,
    lastReviewed: "yesterday",
    status: "almost",
  },
  {
    id: "3",
    word: "loquacious",
    pronunciation: "/ləʊˈkweɪ.ʃəs/",
    partOfSpeech: "adjective",
    definition: "Tending to talk a great deal; talkative.",
    shortDef: "Extremely talkative",
    mnemonic: "LOQUACIOUS — 'loco-for-talking'. Imagine a parrot in a tuxedo who won't stop chatting at a fancy dinner party.",
    examples: [
      "Her loquacious nature made her a natural fit for the debate team.",
      "The loquacious professor often ran ten minutes over the lecture period.",
    ],
    youtubeLinks: [
      { title: "Are You Too Talkative? Signs You Might Be Loquacious", channel: "Psychology Today", duration: "7:21" },
      { title: "Loquacious Characters in Literature", channel: "CrashCourse", duration: "9:45" },
    ],
    masteryLevel: 80,
    lastReviewed: "3 days ago",
    status: "almost",
  },
  {
    id: "4",
    word: "obfuscate",
    pronunciation: "/ˈɒb.fjʊ.skeɪt/",
    partOfSpeech: "verb",
    definition: "To make unclear or confusing; to obscure.",
    shortDef: "To make something confusing or unclear",
    mnemonic: "OBFUSCate — 'OBviously FUSed with Confusion'. Imagine someone deliberately tangling all the wires behind a TV to confuse you.",
    examples: [
      "The politician tried to obfuscate the real issues with irrelevant statistics.",
      "Legal documents often obfuscate simple truths with complex jargon.",
    ],
    youtubeLinks: [
      { title: "How Politicians Obfuscate the Truth", channel: "PolyMatter", duration: "11:38" },
      { title: "Code Obfuscation: Why Developers Hide Their Code", channel: "Fireship", duration: "6:02" },
    ],
    masteryLevel: 15,
    lastReviewed: "1 week ago",
    status: "new",
  },
  {
    id: "5",
    word: "ameliorate",
    pronunciation: "/əˈmiː.li.ə.reɪt/",
    partOfSpeech: "verb",
    definition: "To make something bad or unsatisfactory better; to improve.",
    shortDef: "To make something bad better",
    mnemonic: "AMELIORATE sounds like 'a-MELIORA-te' — from Latin 'melior' meaning better. Think of a MEDAL given to someone who IMPROVED things.",
    examples: [
      "The new policy was designed to ameliorate the living conditions of homeless veterans.",
      "Exercise and proper sleep can ameliorate symptoms of mild depression.",
    ],
    youtubeLinks: [
      { title: "How Urban Green Spaces Ameliorate Air Pollution", channel: "Bloomberg Quicktake", duration: "5:54" },
      { title: "Policies That Ameliorate Income Inequality", channel: "Economics Explained", duration: "14:22" },
    ],
    masteryLevel: 0,
    lastReviewed: "never",
    status: "new",
  },
  {
    id: "6",
    word: "sycophant",
    pronunciation: "/ˈsɪk.ə.fənt/",
    partOfSpeech: "noun",
    definition: "A person who acts obsequiously toward someone important in order to gain advantage; a flatterer.",
    shortDef: "A person who flatters to gain advantage",
    mnemonic: "SYCOPHANT — 'SICK-o-FANT'. A sickeningly fake person who is an elephant in the room — everyone knows they're just flattering the boss.",
    examples: [
      "The CEO surrounded himself with sycophants who never challenged his bad decisions.",
      "True friends will tell you hard truths; sycophants will only tell you what you want to hear.",
    ],
    youtubeLinks: [
      { title: "Sycophants in the Workplace: How to Spot Them", channel: "Harvard Business Review", duration: "8:11" },
      { title: "Historical Sycophants Who Changed History (Not for the Better)", channel: "Weird History", duration: "16:49" },
    ],
    masteryLevel: 55,
    lastReviewed: "4 days ago",
    status: "learning",
  },
  {
    id: "7",
    word: "vociferous",
    pronunciation: "/vəˈsɪf.ər.əs/",
    partOfSpeech: "adjective",
    definition: "Expressing or characterized by vehement opinions; loud and forceful.",
    shortDef: "Loud and forceful in expressing opinions",
    mnemonic: "VOCIFEROUS — 'VOICE-FIERCE-ous'. Someone with a FIERCE VOICE who shouts their opinions at everyone.",
    examples: [
      "Vociferous protesters gathered outside the courthouse demanding justice.",
      "She was a vociferous critic of the new education policy.",
    ],
    youtubeLinks: [
      { title: "Vociferous Fans: The Loudest Stadiums in the World", channel: "ESPN", duration: "9:03" },
      { title: "How to Handle Vociferous Critics in Business", channel: "Inc. Magazine", duration: "6:47" },
    ],
    masteryLevel: 90,
    lastReviewed: "today",
    status: "mastered",
  },
  {
    id: "8",
    word: "perfidious",
    pronunciation: "/pəˈfɪd.i.əs/",
    partOfSpeech: "adjective",
    definition: "Deceitful and untrustworthy; guilty of betrayal.",
    shortDef: "Deceitful and guilty of betrayal",
    mnemonic: "PERFIDIOUS — 'PURR-FIDO-ous'. Imagine a cat named Fido (usually a dog name) — treacherous because it betrays its own nature.",
    examples: [
      "His perfidious allies abandoned him at the most critical moment of the battle.",
      "The perfidious advisor secretly sold state secrets to the enemy.",
    ],
    youtubeLinks: [
      { title: "History's Most Perfidious Betrayals", channel: "Biographics", duration: "20:14" },
      { title: "Perfidious Albion: The History of a Phrase", channel: "History Hit", duration: "7:38" },
    ],
    masteryLevel: 25,
    lastReviewed: "5 days ago",
    status: "learning",
  },
  {
    id: "9",
    word: "laconic",
    pronunciation: "/ləˈkɒn.ɪk/",
    partOfSpeech: "adjective",
    definition: "Using very few words; brief and concise.",
    shortDef: "Using very few words",
    mnemonic: "LACONIC comes from Laconia (ancient Sparta). Spartans were famous for brief, blunt speech. 'If.' — the entire Spartan reply to Philip of Macedon's long threat.",
    examples: [
      "His laconic response — just 'No.' — ended the negotiation immediately.",
      "The general was known for his laconic battle orders.",
    ],
    youtubeLinks: [
      { title: "Why Spartans Were the Masters of Laconic Wit", channel: "Overly Sarcastic Productions", duration: "11:02" },
      { title: "The Art of Saying Less: Laconic Communication", channel: "Big Think", duration: "5:28" },
    ],
    masteryLevel: 70,
    lastReviewed: "yesterday",
    status: "almost",
  },
  {
    id: "10",
    word: "equivocate",
    pronunciation: "/ɪˈkwɪv.ə.keɪt/",
    partOfSpeech: "verb",
    definition: "To use ambiguous language so as to conceal the truth or avoid committing oneself.",
    shortDef: "To speak ambiguously to avoid commitment",
    mnemonic: "EQUIVOCATE — 'equal-voice'. Someone who speaks with 'equal voice' on both sides, never picking one, always hedging their bets.",
    examples: [
      "When asked about the scandal, the senator equivocated, giving vague non-answers.",
      "Stop equivocating and just tell me if you want to come or not.",
    ],
    youtubeLinks: [
      { title: "Politicians Who Equivocate: A Masterclass in Dodging Questions", channel: "Channel 4 News", duration: "8:55" },
      { title: "Equivocation as a Logical Fallacy Explained", channel: "Philosophy Tube", duration: "6:14" },
    ],
    masteryLevel: 40,
    lastReviewed: "3 days ago",
    status: "learning",
  },
  {
    id: "11",
    word: "magnanimous",
    pronunciation: "/mæɡˈnæn.ɪ.məs/",
    partOfSpeech: "adjective",
    definition: "Generous or forgiving, especially toward a rival or less powerful person.",
    shortDef: "Very generous and forgiving",
    mnemonic: "MAGNANIMOUS — 'MAGNA-ANIMUS' — Latin for 'great soul'. Someone with a GREAT BIG SOUL who forgives easily.",
    examples: [
      "In a magnanimous gesture, the champion praised his defeated opponent.",
      "The magnanimous donor gave anonymously, refusing all recognition.",
    ],
    youtubeLinks: [
      { title: "Magnanimous Leaders Who Changed History", channel: "Kings and Generals", duration: "18:30" },
      { title: "The Psychology of Magnanimity", channel: "School of Life", duration: "7:45" },
    ],
    masteryLevel: 50,
    lastReviewed: "2 days ago",
    status: "learning",
  },
  {
    id: "12",
    word: "recalcitrant",
    pronunciation: "/rɪˈkæl.sɪ.trənt/",
    partOfSpeech: "adjective",
    definition: "Having an obstinately uncooperative attitude toward authority or discipline.",
    shortDef: "Stubbornly uncooperative",
    mnemonic: "RECALCITRANT — 'RE-KICKS-TRANT'. Think of a stubborn mule that KICKS BACK every time you try to lead it anywhere.",
    examples: [
      "The recalcitrant student refused to follow any classroom rules.",
      "Negotiators struggled to make progress with the recalcitrant faction.",
    ],
    youtubeLinks: [
      { title: "How to Manage Recalcitrant Employees", channel: "Harvard Business Review", duration: "9:22" },
      { title: "Recalcitrant States in International Relations", channel: "International Relations", duration: "12:07" },
    ],
    masteryLevel: 5,
    lastReviewed: "never",
    status: "new",
  },
];

export const DIAGNOSTIC_WORDS = [
  { word: "ephemeral", definition: "Lasting for a very short time" },
  { word: "pernicious", definition: "Having a harmful, gradual effect" },
  { word: "loquacious", definition: "Extremely talkative" },
  { word: "obfuscate", definition: "To make confusing or unclear" },
  { word: "ameliorate", definition: "To make something bad better" },
  { word: "sycophant", definition: "A flattering person seeking advantage" },
  { word: "vociferous", definition: "Loud and forceful" },
  { word: "perfidious", definition: "Deceitful and untrustworthy" },
  { word: "laconic", definition: "Using very few words" },
  { word: "equivocate", definition: "To speak ambiguously" },
  { word: "magnanimous", definition: "Generous and forgiving" },
  { word: "recalcitrant", definition: "Stubbornly uncooperative" },
  { word: "ubiquitous", definition: "Present everywhere at once" },
  { word: "nefarious", definition: "Wicked and criminal" },
  { word: "languid", definition: "Weak and lacking energy" },
  { word: "perspicacious", definition: "Having keen mental perception" },
  { word: "vituperate", definition: "To criticize harshly" },
  { word: "propitious", definition: "Giving a good sign; favorable" },
  { word: "truculent", definition: "Eager to argue; aggressively defiant" },
  { word: "enervate", definition: "To weaken; drain of energy" },
];

export const WORD_LISTS = [
  {
    id: "toefl-500",
    name: "TOEFL Academic 500",
    description: "Essential vocabulary for the TOEFL iBT exam",
    wordCount: 500,
    difficulty: "Intermediate",
    difficultyColor: "blue",
    progress: 68,
    started: true,
    inPool: 73,
  },
  {
    id: "gre-1000",
    name: "GRE Top 1000",
    description: "High-frequency words on the GRE Verbal section",
    wordCount: 1000,
    difficulty: "Advanced",
    difficultyColor: "purple",
    progress: 0,
    started: false,
    inPool: 0,
  },
  {
    id: "sat-200",
    name: "SAT Essential 200",
    description: "Most commonly tested SAT vocabulary words",
    wordCount: 200,
    difficulty: "Intermediate",
    difficultyColor: "blue",
    progress: 0,
    started: false,
    inPool: 0,
  },
  {
    id: "medical-terms",
    name: "Medical Terminology",
    description: "Core terms for nursing and medical students",
    wordCount: 800,
    difficulty: "Advanced",
    difficultyColor: "purple",
    progress: 0,
    started: false,
    inPool: 0,
  },
  {
    id: "ielts-400",
    name: "IELTS Academic 400",
    description: "Key vocabulary for IELTS Academic Writing & Reading",
    wordCount: 400,
    difficulty: "Intermediate",
    difficultyColor: "blue",
    progress: 0,
    started: false,
    inPool: 0,
  },
  {
    id: "business-300",
    name: "Business English 300",
    description: "Professional vocabulary for business communication",
    wordCount: 300,
    difficulty: "Beginner",
    difficultyColor: "green",
    progress: 0,
    started: false,
    inPool: 0,
  },
];
